import * as THREE from 'three';

/**
 * Watching your team after you die.
 *
 * In a round mode death is final until the next round, so without this you
 * spend up to two minutes looking at the patch of ground your corpse fell on
 * while the round you care about happens somewhere else. CS solves it by
 * putting you behind a living teammate and letting you cycle.
 *
 * It runs entirely AFTER `PlayerController.update` has written the camera, and
 * only while dead. That keeps the death-collapse camera intact for deathmatch —
 * where you respawn in three seconds and never want a chase camera — and means
 * nothing in the player controller has to know spectating exists.
 */

const _target = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _dir = new THREE.Vector3();

/** Seconds of corpse-view before the camera moves on. */
const LINGER = 2.2;
/** Chase distance and height behind the subject. */
const BACK = 2.9;
const UP = 1.75;

export class Spectator {
  constructor(game) {
    this.game = game;
    this.active = false;
    this.subject = null;
    this.since = 0;
    this.pos = new THREE.Vector3();
    this.look = new THREE.Vector3();
    this._seeded = false;
  }

  /**
   * Everyone still alive that the player is entitled to watch.
   *
   * Bots offline, other PLAYERS online. Reading `game.enemies` alone is why
   * spectating did nothing in multiplayer: that list is the bots this process
   * simulates, and online it is deliberately always empty — remote players are
   * rigs in `_netBodies`, which the game never ticks because their decisions
   * are made on someone else's machine. Neither list is the whole answer, so
   * both are searched.
   *
   * Teammates only, in both cases. Watching through an enemy's eyes while your
   * team is still playing the round is a wallhack with extra steps.
   */
  candidates() {
    const g = this.game;
    const team = g.player.team;
    const out = (this._cands ??= []);
    out.length = 0;
    for (const e of g.actors) {
      if (!e.alive || e._removed) continue;
      if (team && e.team !== team) continue;
      out.push(e);
    }
    return out;
  }

  reset() {
    this.active = false;
    this.subject = null;
    this.since = 0;
    this._seeded = false;
  }

  /** Cycle to the next living teammate. `dir` is +1 or -1. */
  cycle(dir = 1) {
    const list = this.candidates();
    if (!list.length) { this.subject = null; return null; }
    const i = list.indexOf(this.subject);
    const n = i < 0 ? 0 : (i + dir + list.length) % list.length;
    this.subject = list[n];
    this._seeded = false;               // snap the camera to the new subject
    return this.subject;
  }

  /**
   * Called from `Game.update` while the player is dead, after the controller
   * has written its own camera.
   */
  update(dt, input) {
    const g = this.game;
    if (g.player.alive) { if (this.active) this.reset(); return; }

    this.since += dt;
    // Let the death collapse play out first — cutting away instantly reads as
    // a bug rather than a transition.
    if (this.since < LINGER) return;

    if (!this.subject || !this.subject.alive || this.subject._removed) this.cycle(1);
    if (!this.subject) { this.active = false; return; }
    this.active = true;

    // Fire or right/left to change subject. Fire is what a CS player reaches
    // for without being told.
    if (input) {
      if (input.mouse?.leftEdge || input.pressed?.('KeyD') || input.pressed?.('Space')) this.cycle(1);
      else if (input.mouse?.rightEdge || input.pressed?.('KeyA')) this.cycle(-1);
    }

    const s = this.subject;
    _target.copy(s.root.position);
    _target.y += 1.35;

    // Sit behind the subject along its facing, and drop the camera out of any
    // wall it would otherwise be buried in.
    _dir.set(Math.sin(s.yaw), 0, Math.cos(s.yaw)).normalize();
    _eye.copy(_target).addScaledVector(_dir, BACK);
    _eye.y = s.root.position.y + UP;

    const hit = g.physics.raycast(_target, _dir, BACK + 0.4);
    if (hit) _eye.copy(_target).addScaledVector(_dir, Math.max(0.6, hit.distance - 0.35));

    if (!this._seeded) {
      this.pos.copy(_eye);
      this.look.copy(_target);
      this._seeded = true;
    } else {
      // Damped follow so a sprinting subject does not whip the camera.
      const k = 1 - Math.exp(-7 * dt);
      this.pos.lerp(_eye, k);
      this.look.lerp(_target, k);
    }

    const cam = g.engine.camera;
    cam.position.copy(this.pos);
    cam.lookAt(this.look);
  }

  /** HUD state: who you are watching, and how many are left. */
  decorate(s) {
    if (!this.active || !this.subject) { s.spectating = null; return; }
    const list = this.candidates();
    s.spectating = {
      name: this.subject.name ?? 'TEAMMATE',
      index: Math.max(0, list.indexOf(this.subject)) + 1,
      count: list.length,
    };
  }
}
